import React from "react";
import {
  Box,
  ButtonBase,
  CircularProgress,
  Typography,
  useTheme,
} from "@mui/material";
import Icon from "../../../Icon/Icon";

/**
 * EGExpandingButton
 *
 * An expandable button with support for dynamic colors using the MUI theme palette.
 * Allows customization of background, icon, and label colors with hover variants,
 * plus special states such as `disabled` and `loading`.
 *
 * @param {string} iconName
 * The name of the icon to render (used by the custom <Icon/> component).
 * @param {string} iconColor
 * Icon color. Can be:
 *  - Palette token: "primary", "secondary.dark", "teal", "blueGray"
 *  - Direct CSS color: "#93d240", "rgb(255,0,0)"
 * Default: inherits `inherit` or a defined contrast color.
 * @param {string} [iconColorHover]
 * Icon color on hover. If not defined, the icon keeps `iconColor`.
 * @param {number} [iconSize=22]
 * Size in pixels of the icon (height and width).
 * @param {boolean} [disabled=false]
 * Disables the button. Applies `0.5` opacity, no hover effects, and no interaction.
 * @param {boolean} [loading=false]
 * Shows a spinner instead of the icon, inheriting the `iconColor`.
 * The button is disabled while `loading=true`.
 * @param {string} label
 * Text that describes the button’s action.
 * @param {string} [labelColor]
 * Label text color.
 * Default: the contrast color calculated from `buttonColor` (`theme.palette.getContrastText`).
 * Can be a palette token or a direct CSS color.
 * @param {string} [labelColorHover]
 * Label text color on hover. If not defined, it keeps `labelColor`.
 * @param {string} [buttonColor="secondary.main"]
 * Background color. Can be:
 *  - Semantic palette token: "primary", "secondary", "error", "success", etc.
 *  - Custom palette token: "teal", "blueGray"
 *  - Direct CSS color: "#93d240"
 * @param {string} [buttonColorHover]
 * Background color on hover:
 *  - If `buttonColor` is semantic (primary, secondary, etc.): automatically uses `.dark`.
 *  - If it’s custom: keeps the same `buttonColor`.
 *  - Can be explicitly overridden with this prop.
 * @param {number} [minCollapsedWidth=48]
 * Minimum width when the button is collapsed.
 * @param {number} [maxExpandedWidth=600]
 * Maximum width when the button is expanded.
 * @param {number} [labelMaxWidthOverride]
 * Overrides the automatic calculation of the maximum available width for the label.
 * @param {object} [sx]
 * Additional styles via MUI’s `sx` API.
 * @param {object} [buttonBaseProps]
 * Additional props passed to `<ButtonBase />` (e.g. `onClick`, `aria-label`, etc.).
 * @returns {JSX.Element} An expandable button styled with MUI.
 * @example
 * // Semantic button with darker hover
 * <EGExpandingButton buttonColor="primary" label="Save" />
 *
 * @example
 * // Button with custom color and label forced to white
 * <EGExpandingButton buttonColor="teal" label="Export" labelColor="white" />
 *
 * @example
 * // Loading button with green spinner
 * <EGExpandingButton buttonColor="blueGray" iconColor="success" loading label="Processing..." />
 */

export const EGExpandingButton = ({
  iconName = "",
  iconColor = "",
  iconColorHover = "",
  iconSize = 22,
  disabled = false,
  label,
  labelColor,
  labelColorHover,
  buttonColor = "",
  buttonColorHover = "",
  loading = false,
  minCollapsedWidth = 48,
  maxExpandedWidth = 600,
  sx = {},
  labelMaxWidthOverride,
  ...buttonBaseProps
}) => {
  const theme = useTheme();

  const paddingX = 8;
  const expandedPaddingRight = 12;
  const collapsedInnerWidth = Math.max(0, minCollapsedWidth - paddingX * 2);
  const computedLabelMaxWidth =
    maxExpandedWidth -
    collapsedInnerWidth -
    paddingX -
    expandedPaddingRight -
    8;
  const labelMax = labelMaxWidthOverride ?? Math.max(0, computedLabelMaxWidth);

  // -----> Helpers <----- //
  const isRawColor = (s) =>
    !s ? false : /^#|^rgb|^hsl|^var\(|^\w+\(.*\)/i.test(s);
  const hasDot = (s) => typeof s === "string" && s.includes(".");

  const tokenToCssToken = (name) => {
    if (!name) return null;
    if (hasDot(name)) return name;
    if (isRawColor(name)) return name;
    return `${name}.main`;
  };

  const resolvePaletteValue = (cssTokenOrColor) => {
    if (!cssTokenOrColor) return null;
    if (isRawColor(cssTokenOrColor)) return cssTokenOrColor;

    if (hasDot(cssTokenOrColor)) {
      const [name, variant] = cssTokenOrColor.split(".");
      const pal = theme.palette[name];
      if (pal) {
        if (typeof pal === "string") return pal;
        return pal[variant] ?? pal.main ?? Object.values(pal)[0];
      }
    }

    const pal = theme.palette[cssTokenOrColor];
    if (pal) {
      if (typeof pal === "string") return pal;
      return pal.main ?? Object.values(pal)[0];
    }

    return cssTokenOrColor;
  };

  // -----> Background <---- //
  const bgToken = tokenToCssToken(buttonColor) ?? "secondary.main";
  let hoverToken;
  if (buttonColorHover) {
    hoverToken = tokenToCssToken(buttonColorHover);
  } else {
    const semanticDarkKeys = new Set([
      "primary",
      "secondary",
      "error",
      "warning",
      "info",
      "success",
    ]);
    const baseName = hasDot(bgToken) ? bgToken.split(".")[0] : bgToken;
    if (semanticDarkKeys.has(baseName)) {
      hoverToken = `${baseName}.dark`;
    } else {
      hoverToken = tokenToCssToken(buttonColor) ?? "secondary.main";
    }
  }

  const realBgColor =
    resolvePaletteValue(bgToken) || resolvePaletteValue("secondary.main");
  let textColor;
  try {
    textColor = theme.palette.getContrastText(realBgColor);
  } catch {
    textColor = "primary.contrastText";
  }

  // ----->  Label <----- //
  const labelToken = tokenToCssToken(labelColor) ?? null;
  const realLabelColor =
    resolvePaletteValue(labelToken) ?? labelColor ?? textColor; // fallback al contraste calculado

  let realLabelHoverColor = realLabelColor;
  if (labelColorHover) {
    const labelHoverToken = tokenToCssToken(labelColorHover);
    realLabelHoverColor =
      resolvePaletteValue(labelHoverToken) ?? realLabelHoverColor;
  }

  // -----> Icon <----- //
  const iconToken = tokenToCssToken(iconColor) ?? null;
  const realIconColor =
    resolvePaletteValue(iconToken) ?? iconColor ?? "inherit";

  let iconHoverToken;
  if (iconColorHover) {
    iconHoverToken = tokenToCssToken(iconColorHover);
  } else {
    // if not defined, use the same as normal
    iconHoverToken = iconToken;
  }
  const realIconHoverColor =
    resolvePaletteValue(iconHoverToken) ?? realIconColor;

  return (
    <ButtonBase
      disabled={disabled || loading}
      {...buttonBaseProps}
      sx={{
        position: "relative",
        display: "flex",
        alignItems: "center",
        justifyContent: "flex-start",
        overflow: "hidden",
        whiteSpace: "nowrap",
        minWidth: minCollapsedWidth,
        maxWidth: minCollapsedWidth,
        padding: `${paddingX}px`,
        borderRadius: 1,
        bgcolor: bgToken,
        color: textColor,
        opacity: disabled || loading ? 0.5 : 1,
        pointerEvents: disabled || loading ? "none" : "auto",
        cursor: disabled || loading ? "default" : "pointer",
        transition:
          "max-width 280ms ease, padding 280ms ease, background-color 160ms ease, color 160ms ease",
        "&:hover, &:focus-visible": disabled
          ? {} // if it is not defined, use the same as usual
          : {
              maxWidth: maxExpandedWidth,
              padding: `${paddingX}px 12px`,
              textDecoration: "none",
              bgcolor: hoverToken,
            },
        "&:hover .expandingLabel, &:focus-visible .expandingLabel": disabled
          ? {}
          : {
              maxWidth: `${labelMax}px`,
              opacity: 1,
              marginLeft: 3,
              color: realLabelHoverColor,
            },
        "&:hover .expandingIcon, &:focus-visible .expandingIcon": disabled
          ? {}
          : {
              color: realIconHoverColor,
            },
        ...sx,
      }}
    >
      <Box
        sx={{
          width: `${collapsedInnerWidth}px`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        {loading ? (
          <CircularProgress
            size={iconSize}
            thickness={4}
            sx={{ color: realIconColor }}
          />
        ) : (
          <Icon
            className="expandingIcon"
            name={iconName}
            color={realIconColor}
            size={iconSize}
          />
        )}
      </Box>

      <Typography
        component="span"
        className="expandingLabel"
        textAlign="center"
        fontWeight={600}
        fontSize={15}
        noWrap
        sx={{
          display: "inline-block",
          maxWidth: 0,
          opacity: 0,
          overflow: "hidden",
          verticalAlign: "middle",
          color: realLabelColor,
          transition:
            "max-width 280ms ease, opacity 160ms ease, margin 280ms ease, color 160ms ease",
          willChange: "max-width, opacity, color",
          "&:hover, &:focus-visible": {
            color: realLabelHoverColor,
          },
        }}
      >
        {label}
      </Typography>
    </ButtonBase>
  );
};
